import type { Exercise } from "../api/types";
import { cloneRow } from "./exerciseRows";

/** Copy exercise fields for a new workout (no ids). Notes off by default. */
export function cloneExercises(
  exercises: Exercise[],
  opts?: { copyNotes?: boolean },
): Exercise[] {
  return exercises.map((e, i) => cloneRow(e, i, opts));
}

export function todayISO(d = new Date()): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Monday 00:00 local of the week containing `d`. */
export function startOfWeekMonday(d = new Date()): Date {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const day = x.getDay(); // 0 Sun … 6 Sat
  const diff = day === 0 ? -6 : 1 - day;
  x.setDate(x.getDate() + diff);
  x.setHours(0, 0, 0, 0);
  return x;
}

export function addDays(d: Date, n: number): Date {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

/**
 * Previous calendar week for API range filters.
 * `from` inclusive, `to` exclusive (this week's Monday) — matches listCompleted.
 */
export function lastWeekRangeISO(now = new Date()): { from: string; to: string } {
  const thisMon = startOfWeekMonday(now);
  const lastMon = addDays(thisMon, -7);
  return { from: todayISO(lastMon), to: todayISO(thisMon) };
}

/**
 * Map a past workout date to the same weekday on the current week.
 * e.g. last Wednesday → this Wednesday.
 */
export function sameWeekdayThisWeek(isoDate: string, now = new Date()): string {
  const src = new Date(isoDate.slice(0, 10) + "T12:00:00");
  const thisMon = startOfWeekMonday(now);
  const day = src.getDay(); // 0 Sun
  const offset = day === 0 ? 6 : day - 1; // Mon=0 … Sun=6
  return todayISO(addDays(thisMon, offset));
}

/** Build create-form initial value from a completed/planned workout source. */
export function buildRepeatInitial(
  source: {
    title: string;
    notes?: string | null;
    exercises: Exercise[];
  },
  opts: { date: string; copyNotes?: boolean },
) {
  return {
    title: source.title,
    notes: opts.copyNotes ? (source.notes ?? "") : "",
    date: opts.date,
    exercises: cloneExercises(source.exercises, {
      copyNotes: opts.copyNotes,
    }),
    durationMinutes: null as number | null,
    startedAt: null as string | null,
    finishedAt: null as string | null,
  };
}

export function repeatCompletedHref(opts: {
  id?: string;
  fromLast?: boolean;
  date?: string;
}): string {
  const q = new URLSearchParams();
  if (opts.fromLast) q.set("fromLast", "1");
  if (opts.id) q.set("fromCompleted", opts.id);
  if (opts.date) q.set("date", opts.date);
  const s = q.toString();
  return `/workouts/new/completed${s ? `?${s}` : ""}`;
}

export function repeatPlannedHref(opts: {
  id?: string;
  fromCompletedId?: string;
  fromPlannedId?: string;
  fromLast?: boolean;
  date?: string;
}): string {
  const q = new URLSearchParams();
  if (opts.fromLast) q.set("fromLast", "1");
  if (opts.fromCompletedId) q.set("fromCompleted", opts.fromCompletedId);
  if (opts.fromPlannedId ?? opts.id) {
    q.set("fromPlanned", opts.fromPlannedId ?? opts.id!);
  }
  if (opts.date) q.set("date", opts.date);
  const s = q.toString();
  return `/workouts/new/planned${s ? `?${s}` : ""}`;
}
